import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable, switchMap, tap } from 'rxjs';

import { PrivaQueryParamsSearch } from '@priva/components/filter';

import { updateGreenHouseMapFilters } from 'app/state/green-house-map/green-house-map.actions';
import {
    selectFilteredGreenHouseMapWithRowNumbers,
    selectGreenHouseMapList,
    selectSearchParams,
} from 'app/state/green-house-map/green-house-map.selectors';
import {
    defaultSearchPrivaQueryParamsSearch,
    GreenHouseMapFilters,
} from 'app/state/green-house-map/greenhouse-map.state';

import { GreenHouseMap, Row } from '../../features/map-row-selector/models';
import { greenHouseMapToRowList } from '../../features/map-row-selector/utils';

export interface MapFilterState {
    selectedGreenHouseMap: GreenHouseMap | undefined;
    filteredRows: number[];
    search: PrivaQueryParamsSearch;
}

const initialState: MapFilterState = {
    selectedGreenHouseMap: undefined,
    filteredRows: [],
    search: defaultSearchPrivaQueryParamsSearch,
};

@Injectable()
export class MapFilterStore extends ComponentStore<MapFilterState> {
    greenHouseMaps$ = this.store.select(selectGreenHouseMapList);
    searchParams$ = this.store.select(selectSearchParams);
    greenHouseMapNumber$ = this.store.select(selectFilteredGreenHouseMapWithRowNumbers);
    constructor(private store: Store) {
        super(initialState);
        this.initializeSelectedGreenHouseMap();
        this.syncFiltersToGlobalStore();
    }

    selectedGreenHouseMap$ = this.select((state) => state.selectedGreenHouseMap);

    mapRows$ = this.selectedGreenHouseMap$.pipe(
        map((selectedGreenHouseMap) =>
            selectedGreenHouseMap ? greenHouseMapToRowList(selectedGreenHouseMap) : [],
        ),
    );

    updateSelectedGreenHouseMap = this.updater((state, selectedGreenHouseMap: GreenHouseMap) =>
        state.selectedGreenHouseMap?.number === selectedGreenHouseMap.number
            ? state
            : {
                  ...state,
                  selectedGreenHouseMap,
                  filteredRows: [],
              },
    );

    updateSearch = this.updater((state, search: PrivaQueryParamsSearch | undefined) => ({
        ...state,
        search: search ?? defaultSearchPrivaQueryParamsSearch,
    }));

    initializeSelectedGreenHouseMap = this.effect((trigger$: Observable<void>) =>
        trigger$.pipe(
            switchMap(() =>
                combineLatest([
                    this.selectedGreenHouseMap$,
                    this.greenHouseMaps$,
                    this.searchParams$,
                    this.greenHouseMapNumber$,
                ]),
            ),
            filter(
                ([selectedGreenHouseMap, greenHouseMaps]) =>
                    !selectedGreenHouseMap && greenHouseMaps.length > 0,
            ),
            tap(([, greenHouseMaps, searchParams, { map }]) => {
                map === undefined
                    ? this.updateSelectedGreenHouseMap(greenHouseMaps[0])
                    : this.updateSelectedGreenHouseMap(map);
                this.updateSearch(searchParams);
            }),
        ),
    );

    syncFiltersToGlobalStore = this.effect((trigger$) =>
        trigger$.pipe(
            switchMap(() => this.state$),
            tap(({ selectedGreenHouseMap, filteredRows, search }) => {
                const greenHouseMapFilters: Partial<GreenHouseMapFilters> | undefined = selectedGreenHouseMap
                    ? {
                          mapNumber: selectedGreenHouseMap.number,
                          filteredRowNumbers: filteredRows,
                          searchParams: search,
                      }
                    : undefined;
                this.store.dispatch(updateGreenHouseMapFilters({ greenHouseMapFilters }));
            }),
        ),
    );

    updateFilteredRows = this.updater((state, filteredRows: Row[]) => ({
        ...state,
        filteredRows: filteredRows.map(({ number }) => number),
    }));
    updateSearchQuery = this.updater((state, search: PrivaQueryParamsSearch | undefined) => ({
        ...state,
        search: search ?? defaultSearchPrivaQueryParamsSearch,
    }));
}
